/**
 * Overview Presenter Component
 * Presentational component for overview tab content
 */

import React from 'react';
import { motion } from 'framer-motion';
import { spacing, typographyColors } from '@/design-system/utils';
import { OverviewLoader } from './OverviewLoader';
import { OverviewIntroSection } from './OverviewIntroSection';
import { DestinationOverviewSection } from './DestinationOverviewSection';
import { EmergencyContactsSection } from './EmergencyContactsSection';
import { FestivalsAndCelebrationsSection } from './FestivalsAndCelebrationsSection';

export interface OverviewPresenterProps {
  overview: {
    name: string;
    description: string;
    pointers?: Array<{
      title: string;
      items: Array<{
        title: string;
        value: string;
        icon?: string;
        tag?: string;
        subtitle?: string;
      }>;
    }>;
    imageUrls?: string[];
  };
  festivals: Array<{
    title: string;
    description: string;
    longDescription: string;
    imageUrls: string[];
  }>;
  placesOfInterest: Array<{
    title: string;
    description: string;
    longDescription: string;
    imageUrls: string[];
  }>;
  isLoading: boolean;
  hasOverviewRequestParams: boolean;
  placeName?: string | null;
}

export const OverviewPresenter: React.FC<OverviewPresenterProps> = ({
  overview,
  festivals,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  placesOfInterest: _placesOfInterest, // Unused - section hidden per design requirements
  isLoading,
  hasOverviewRequestParams,
  placeName,
}) => {
  // Check if we have data to display
  // Show description even if it's placeholder data
  const hasDescription =
    overview.description && overview.description.trim() !== '';

  // Use name if available, otherwise fall back to placeName or default
  const displayName =
    overview.name &&
    overview.name.trim() !== '' &&
    overview.name !== 'Unknown Location'
      ? overview.name
      : placeName && placeName.trim() !== ''
        ? placeName
        : 'Location Overview';

  const hasOverviewData =
    hasDescription ||
    (overview.name && overview.name.trim() !== '') ||
    (overview.pointers && overview.pointers.length > 0) ||
    (overview.imageUrls && overview.imageUrls.length > 0) ||
    (festivals && festivals.length > 0);
  // placesOfInterest is hidden per design requirements

  if (isLoading) {
    return <OverviewLoader />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
        width: '100%',
        boxSizing: 'border-box',
        alignItems: 'stretch',
      }}
    >
      {/* Overview Intro Section - Show if we have description */}
      {hasDescription && (
        <OverviewIntroSection
          title={displayName}
          description={overview.description}
        />
      )}

      {/* Destination Overview Section */}
      {overview.pointers?.[0]?.items &&
        overview.pointers[0].items.length > 0 && (
          <DestinationOverviewSection items={overview.pointers[0].items} />
        )}

      {/* Emergency Contacts Section */}
      {overview.pointers?.[1]?.items &&
        overview.pointers[1].items.length > 0 && (
          <EmergencyContactsSection
            contacts={overview.pointers[1].items.map(item => ({
              title: item.title,
              value: item.value,
              icon: item.icon || undefined, // Pass icon string if available
            }))}
          />
        )}

      {/* Festivals & Celebrations Section */}
      {festivals && festivals.length > 0 && (
        <FestivalsAndCelebrationsSection festivals={festivals} />
      )}

      {/* Places of Interest Section - Hidden per design requirements */}
      {/* {placesOfInterest && placesOfInterest.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: 0.3,
            ease: [0.4, 0, 0.2, 1],
            opacity: { duration: 0.4 },
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[3],
          }}
        >
          <SectionHeader title='Places of Interest' />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[2],
            }}
          >
            {placesOfInterest.map((place, index) => (
              <FestivalCard
                key={index}
                festival={{
                  title: place.title,
                  description: place.description,
                  longDescription: place.longDescription,
                  imageUrls: place.imageUrls,
                }}
                index={index}
              />
            ))}
          </div>
        </motion.div>
      )} */}

      {/* Show message if no data available */}
      {!hasOverviewData && !isLoading && hasOverviewRequestParams && (
        <div
          style={{
            padding: spacing[4],
            textAlign: 'center',
            color: typographyColors.secondary,
          }}
        >
          No overview data available for this location.
        </div>
      )}
    </motion.div>
  );
};
